import { useEffect, useState } from 'react';
import type { Operaciones } from '../../../api/operaciones';
import type { ResumenDeCliente } from '../../../api/ops/clientesDuplicados';
import type { TipoSolicitud } from '../../../api/ops/clientesPrivacidad';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Ayuda } from '../../../componentes/Ayuda';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Fecha } from '../../../componentes/Fecha';
import { Icono } from '../../../componentes/Icono';
import { OpcionRadio } from '../../../componentes/OpcionRadio';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { hoyLocal } from '../../../logica/fechasCortas';
import { textoDeError } from '../../equipo/textos';
import { AYUDA, N, TEXTOS, TIPO } from './textos';

interface Props {
    ops: Operaciones;
    negocio: string;
    nombreNegocio: string;
    movil: boolean;
    abierto: boolean;
    onCerrar: () => void;
    onRegistrada: () => void;
}

function inicialesDe(nombre: string): string {
    return nombre
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p.charAt(0).toLocaleUpperCase('es'))
        .join('');
}

/** «Recibida el» como instante: hoy, ahora; otro día, a mediodía de ese día (el plazo de un mes cuenta desde él). */
function recibidaEl(dia: string): string {
    return dia === hoyLocal() ? new Date().toISOString() : new Date(`${dia}T12:00:00`).toISOString();
}

/**
 * Registrar una solicitud que llega por otra vía (RV Panel de formulario): el cliente (buscado en el negocio), qué pide,
 * «Recibida el» (solo fechas pasadas), cómo se comprobó la identidad (N5: una referencia, no el documento) y, si se
 * quiere, lo que escribió. Solo la relación con el negocio, nunca la Cuenta Reservaria (N5).
 */
export function PanelRegistrar({ ops, negocio, nombreNegocio, movil, abierto, onCerrar, onRegistrada }: Props) {
    const avisar = useAvisoBreve();
    const [visto, setVisto] = useState(false);
    const [cliente, setCliente] = useState<ResumenDeCliente | null>(null);
    const [busqueda, setBusqueda] = useState('');
    const [resultados, setResultados] = useState<ResumenDeCliente[] | null>(null);
    const [tipo, setTipo] = useState<TipoSolicitud | null>(null);
    const [dia, setDia] = useState(hoyLocal());
    const [prueba, setPrueba] = useState('');
    const [conDetalles, setConDetalles] = useState(false);
    const [detalles, setDetalles] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);

    if (abierto !== visto) {
        setVisto(abierto);
        if (abierto) {
            setCliente(null);
            setBusqueda('');
            setResultados(null);
            setTipo(null);
            setDia(hoyLocal());
            setPrueba('');
            setConDetalles(false);
            setDetalles('');
            setIntentado(false);
        }
    }

    useEffect(() => {
        const q = busqueda.trim();
        if (!abierto || cliente !== null) {
            return;
        }
        let vivo = true;
        const reloj = setTimeout(() => {
            ops.listCustomers(negocio, q.length >= 2 ? { q, limit: 6 } : { limit: 4 })
                .then((l) => {
                    if (vivo) {
                        setResultados(l.data.filter((c) => c.status !== 'anonymized' && c.status !== 'merged'));
                    }
                })
                .catch(() => undefined);
        }, q === '' ? 0 : 250);
        return () => {
            vivo = false;
            clearTimeout(reloj);
        };
    }, [ops, negocio, abierto, cliente, busqueda]);

    const errores = {
        cliente: cliente === null ? 'Elige el cliente.' : null,
        tipo: tipo === null ? 'Elige qué pide.' : null,
        prueba: prueba.trim().length < 3 ? 'Apunta cómo lo comprobaste.' : null,
        dia: dia === '' ? 'Elige el día.' : null,
    };

    const registrar = () => {
        setIntentado(true);
        if (cliente === null || tipo === null || errores.prueba !== null || errores.dia !== null) {
            return;
        }
        setOcupado(true);
        ops.registerPrivacyRequest(negocio, {
            customer_id: cliente.id,
            type: tipo,
            received_at: recibidaEl(dia),
            identity_evidence_ref: prueba.trim(),
            ...(detalles.trim() === '' ? {} : { details: detalles.trim() }),
        })
            .then(() => {
                avisar({ tipo: 'ok', texto: 'Solicitud registrada.' });
                onRegistrada();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido registrar la solicitud. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    return (
        <PanelLateral abierto={abierto} onCerrar={onCerrar} movil={movil} titulo="Registrar solicitud" eyebrow="Nueva solicitud" subtitulo="La que te llega por email o en persona." icono="shield-check" accion="Registrar" ocupado={ocupado} ocupadoTexto="Registrando…" onAccion={registrar}>
            <div className="rv-ca-cuerpo">
                <div className="rv-pr-panel-cab">
                    <p className="rv-ca-ceja">Datos de la solicitud</p>
                    <Ayuda tarjetas={AYUDA.registrar} />
                </div>
                <div className="rv-ca-buscar">
                    <strong className="rv-pr-subtitulo">Cliente</strong>
                    {cliente !== null ? (
                        <div className="rv-ca-elegido">
                            <span className="rv-ca-inicial">{inicialesDe(cliente.display_name)}</span>
                            <span>
                                <strong>{cliente.display_name}</strong>
                                <small>{[cliente.phone ?? '', cliente.email ?? ''].filter((t) => t !== '').join(' · ')}</small>
                            </span>
                            <Boton tipo="secundario" icono="x" onClick={() => { setCliente(null); }}>
                                Cambiar
                            </Boton>
                        </div>
                    ) : (
                        <>
                            <Campo tipo="search" etiqueta="Nombre, teléfono o email" valor={busqueda} onChange={setBusqueda} nombre="buscar-cliente-solicitud" error={intentado ? errores.cliente : null} />
                            {resultados !== null && resultados.length > 0 && (
                                <div className="rv-ca-resultados">
                                    {resultados.map((r) => (
                                        <button key={r.id} type="button" className="rv-ca-resultado" onClick={() => { setCliente(r); }}>
                                            <span className="rv-ca-inicial">{inicialesDe(r.display_name)}</span>
                                            <span>
                                                <strong>{r.display_name}</strong>
                                                <small>{[r.phone ?? '', r.email ?? ''].filter((t) => t !== '').join(' · ')}</small>
                                            </span>
                                            <Icono nombre="plus" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div className="rv-ca-bloque">
                    <strong className="rv-pr-subtitulo">Qué pide</strong>
                    <div role="radiogroup" aria-label="Qué pide" className="rv-pr-tipos">
                        {(['access', 'rectification', 'erasure'] as const).map((t) => (
                            <OpcionRadio key={t} elegida={tipo === t} icono={TIPO[t].icono} titulo={TIPO[t].nombre} texto={TIPO[t].ejemplo} onElegir={() => { setTipo(t); }} />
                        ))}
                    </div>
                    {intentado && errores.tipo !== null && (
                        <span role="alert" className="rv-campo__error">
                            <Icono nombre="circle-alert" />
                            <span>{errores.tipo}</span>
                        </span>
                    )}
                    {/* N5: solo su relación con el negocio; la Cuenta Reservaria, desde «Mis datos» o con Reservaria. */}
                    <p className="rv-ca-nota rv-ca-nota--gris">
                        <Icono nombre="lock" />
                        <span>{N.n5Ambito(nombreNegocio)}</span>
                    </p>
                </div>
                <Fecha etiqueta="Recibida el" valor={dia} onChange={setDia} max={hoyLocal()} ayuda={TEXTOS.recibidaAyuda} error={intentado ? errores.dia : null} nombre="recibida-el" />
                <Campo etiqueta={N.n5Etiqueta} valor={prueba} onChange={setPrueba} max={200} ayuda={N.n5Ayuda} error={intentado ? errores.prueba : null} nombre="prueba-identidad" />
                {conDetalles ? (
                    <Campo etiqueta="Detalles (opcional)" valor={detalles} onChange={setDetalles} filas={3} max={2000} ayuda={TEXTOS.detallesAyuda} nombre="detalles-solicitud" />
                ) : (
                    <Boton tipo="fantasma" icono="plus" className="rv-pr-detalles" onClick={() => { setConDetalles(true); }}>
                        Añadir detalles (opcional)
                    </Boton>
                )}
            </div>
        </PanelLateral>
    );
}
